import React, { useCallback, useContext, useMemo } from 'react';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { FormattedMessage } from 'react-intl';
import { NumberInput } from 'ui-components/components/number-input';
import { BATCH_BY_ORDER_COUNT } from '../constants/auto-batching-constants';
import { AutoBatchingContext } from '../context';
import { InlineMessage } from 'ui-components/components/inline-message';
import { useYupFormValidation } from '../../../../validation/use-yup-form-validation';
import { AutoBatchHelperTip } from './helper-tip';
import classnames from 'classnames';
import messages from './messages';
import style from '../style.module.less';

const BatchByOrderCountRadioOption = () => {
  const {
    autoBatchingOptionType,
    setAutoBatchingOptionType,
    numberOfOrdersPerBatch,
    setNumberOfOrdersPerBatch,
    setNumberOfBatches,
    getAnticipatedCountFromTotalRecords,
    hasBatchWithInconsistentOrderCount,
    yupSchema,
  } = useContext(AutoBatchingContext);
  const isDisabled = autoBatchingOptionType !== BATCH_BY_ORDER_COUNT;

  const onSetAutoBatchingOptionType = useCallback(() => {
    setAutoBatchingOptionType(BATCH_BY_ORDER_COUNT);
    setNumberOfOrdersPerBatch(0);
    setNumberOfBatches(0);
  }, [setNumberOfOrdersPerBatch, setNumberOfBatches, setAutoBatchingOptionType]);

  const form = useMemo(() => ({ [BATCH_BY_ORDER_COUNT]: numberOfOrdersPerBatch }), [numberOfOrdersPerBatch]);
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form,
    schema: yupSchema,
  });

  const currentOptionIsActive = autoBatchingOptionType === BATCH_BY_ORDER_COUNT;
  const anticipatedCount = getAnticipatedCountFromTotalRecords(numberOfOrdersPerBatch);
  const shouldShowHelperTip = currentOptionIsActive && numberOfOrdersPerBatch > 0;
  const shouldShowInlineError = !isValid && validationErrors[BATCH_BY_ORDER_COUNT];
  const shouldShowInconsistentCountWarning = !shouldShowInlineError && currentOptionIsActive && hasBatchWithInconsistentOrderCount();
  const inputClasses = classnames(style['auto-batch-input'], {
    [style['has-error']]: shouldShowInlineError,
  });

  return (
    <div className={style['radio-input']}>
      <RadioCheckListItem className={style['auto-batch-radio']} isChecked={currentOptionIsActive} onChecked={onSetAutoBatchingOptionType}>
        <FormattedMessage {...messages.autoBatchingNumberOrdersPerBatch} />
      </RadioCheckListItem>
      <div>
        <div className={style['number-and-tip']}>
          <NumberInput
            className={inputClasses}
            disabled={isDisabled}
            min={0}
            onChange={setNumberOfOrdersPerBatch}
            value={numberOfOrdersPerBatch}
          />
          {shouldShowHelperTip && <AutoBatchHelperTip anticipatedCount={anticipatedCount} optionType={BATCH_BY_ORDER_COUNT} />}
        </div>
        {shouldShowInlineError && (
          <InlineMessage type='error'>
            <FormattedMessage {...validationErrors[BATCH_BY_ORDER_COUNT].errors[0]} />
          </InlineMessage>
        )}
        {shouldShowInconsistentCountWarning && (
          <InlineMessage className={style['inline-limit-width']} type='info'>
            <FormattedMessage {...messages.autoBatchingWillResultInUnevenOrderCount} />
          </InlineMessage>
        )}
      </div>
    </div>
  );
};

export { BatchByOrderCountRadioOption };
